import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';

import { getCatalogRunArtifact } from '../../api/catalogs';
import { SNAPSHOT_VIEW_MAX_GZIP_BYTES, downloadTextFile, openJsonInTab, openTextInTab } from './catalogArtifactFiles';
import './CatalogRunHistory.scss';

const STATUS_WORDS = {
  queued: 'Queued',
  running: 'Running',
  publishing: 'Publishing',
  ready: 'Ready',
  succeeded: 'Succeeded',
  published: 'Published',
  failed: 'Failed',
  needs_repair: 'Needs repair'
};

const CatalogRunHistory = ({ brandKey, BrandIntegration_id, runs }) => (
  <div className='catalogs-run-history'>
    {!runs && <div className='catalogs-run-history-empty'>Loading history…</div>}
    {runs?.length === 0 && <div className='catalogs-run-history-empty'>No catalog activity recorded yet.</div>}
    {Boolean(runs?.length) && (
      <div className='catalogs-run-history-rows'>
        {runs.map(run => <RunRow BrandIntegration_id={BrandIntegration_id} brandKey={brandKey} key={run._id} run={run} />)}
      </div>
    )}
  </div>
);

const RunRow = ({ brandKey, BrandIntegration_id, run }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasVersion = Boolean(run.number && run.tree?.s3Key && brandKey && BrandIntegration_id);
  const summary = describeRowSummary(run);
  const toggleDetail = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded(open => !open);
  };
  return (
    <div className='catalogs-run-history-item'>
      <div className='catalogs-run-history-row' onClick={toggleDetail}>
        <button
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${run.number ? `version ${run.number}` : 'run'} details`}
          className={cn('catalogs-run-history-expander', { open: isExpanded })}
          onClick={toggleDetail}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
        <span className='catalogs-run-history-number'>
          {run.number ? `#${run.number}` : run.kind === 'snapshot' ? 'Snapshot' : 'Build'}
          {run.role && <span className={cn('catalogs-run-history-role', run.role)}>{roleLabel(run.role)}</span>}
        </span>
        <span className='catalogs-run-history-summary'>
          <strong>{summary.primary}</strong>
          {summary.secondary && <span>{summary.secondary}</span>}
        </span>
        <span className='catalogs-run-history-action'>
          {hasVersion && (
            <Link
              className='catalogs-button ghost compact'
              to={`/catalogs/${brandKey}/integration-${BrandIntegration_id}/run-${run.number}`}
              onClick={event => event.stopPropagation()}
            >
              {run.role === 'live' ? 'View current' : run.role === 'previously_live' ? 'Compare version' : 'Review draft'}
            </Link>
          )}
        </span>
      </div>
      {isExpanded && <RunRowDetail BrandIntegration_id={BrandIntegration_id} brandKey={brandKey} run={run} />}
    </div>
  );
};

const RunRowDetail = ({ brandKey, BrandIntegration_id, run }) => {
  const [busyKey, setBusyKey] = useState(null);

  const fetchArtifact = async (kind, mode) => {
    setBusyKey(`${kind}-${mode}`);
    try {
      const artifact = await getCatalogRunArtifact(brandKey, BrandIntegration_id, run._id, kind);
      const runLabel = run.number ? `version-${run.number}` : `run-${run._id}`;
      if (mode === 'download') return downloadTextFile(`${brandKey}-${runLabel}-${kind}.${kind === 'script' ? 'py' : 'json'}`, artifact.content);
      if (kind === 'snapshot') return openJsonInTab(artifact.content);
      openTextInTab(artifact.content);
    } catch (artifactError) {
      cogoToast.error(artifactError, { hideAfter: 10 });
    } finally {
      setBusyKey(null);
    }
  };

  const artifacts = [
    run.snapshot?.s3Key
      ? { kind: 'snapshot', label: describeSnapshot(run.snapshot), isViewable: !(run.snapshot.bytes > SNAPSHOT_VIEW_MAX_GZIP_BYTES) }
      : null,
    run.script?.s3Key
      ? { kind: 'script', label: `Script · ${run.script?.name ? `${run.script.name} · ` : ''}${fingerprintLabel(run.script)}`, isViewable: true }
      : null
  ].filter(Boolean);
  return (
    <div className='catalogs-run-history-detail'>
      <div className='catalogs-run-history-detail-outcome'>{describeOutcome(run)}</div>
      <div className='catalogs-run-history-detail-meta'>
        <span>{`Requested ${formatStamp(run.queuedAt)} by ${run.requestedBy || '—'}`}</span>
        {describeElapsed(run) !== '—' && <span>{`Total elapsed ${describeElapsed(run)}`}</span>}
        {run.kind !== 'snapshot' && <span>{`${run.kind === 'sync' ? 'Sync now' : 'Custom version'} · Auto-publish ${run.autoPublish ? 'on' : 'off'}`}</span>}
      </div>
      <div className='catalogs-run-history-detail-inputs'>{describeInputs(run)}</div>
      <RunSteps steps={run.steps} />
      {describeSearchSync(run) && <div className='catalogs-run-history-detail-inputs'>{describeSearchSync(run)}</div>}
      {(run.errors || []).map((error, index) => (
        <div className='catalogs-run-history-error' key={`${error}-${index}`}>{error}</div>
      ))}
      {(run.checks?.findings || []).map(finding => (
        <div className='catalogs-run-history-finding' key={finding.key}>{finding.message}</div>
      ))}
      {run.stderrTail && <pre className='catalogs-run-history-stderr'>{run.stderrTail}</pre>}
      {run.livePeriods?.map((period, index) => (
        <div className='catalogs-run-history-period' key={`${period.startedAt}-${index}`}>{describeLivePeriod(period)}</div>
      ))}
      {artifacts.map(({ kind, label, isViewable }) => (
        <div className='catalogs-run-history-artifact' key={kind}>
          <span>{label}</span>
          {isViewable && (
            <button className='catalogs-button ghost compact' disabled={busyKey === `${kind}-view`} onClick={() => fetchArtifact(kind, 'view')}>
              {busyKey === `${kind}-view` ? 'Loading' : 'View'}
            </button>
          )}
          <button className='catalogs-button ghost compact' disabled={busyKey === `${kind}-download`} onClick={() => fetchArtifact(kind, 'download')}>
            {busyKey === `${kind}-download` ? 'Loading' : 'Download'}
          </button>
        </div>
      ))}
    </div>
  );
};

const RunSteps = ({ steps }) => {
  const entries = Object.entries(steps || {}).filter(([, step]) => step.status !== 'not_requested');
  if (!entries.length) return null;
  return (
    <div className='catalogs-run-history-steps'>
      {entries.map(([key, step]) => (
        <span className={step.status} key={key}>{describeStep(key, step)}</span>
      ))}
    </div>
  );
};

const roleLabel = role => ({ live: 'Live', previously_live: 'Previously live', draft: 'Draft' }[role] || role);
const stepLabel = step => ({ snapshot: 'Snapshot', build: 'Build', checks: 'Checks', publish: 'Publish' }[step] || step);
const formatStamp = value => (value ? moment(value).format('MMM D, YYYY h:mm A') : '—');
const fingerprintLabel = artifact => String(artifact?.fingerprint || '').slice(0, 8) || '—';

const describeSnapshot = snapshot => {
  const facts = [formatStamp(snapshot?.generatedAt)];
  if (snapshot?.productCount != null) facts.push(`${Number(snapshot.productCount).toLocaleString()} products`);
  facts.push(fingerprintLabel(snapshot));
  return `Snapshot · ${facts.join(' · ')}`;
};

const describeInputs = run => {
  if (run.kind === 'snapshot') return `Standalone snapshot · requested ${formatStamp(run.queuedAt)}`;
  const snapshot = run.snapshot?.generatedAt ? `${formatStamp(run.snapshot.generatedAt)} snapshot` : 'Snapshot pending';
  const script = run.script?.name || (run.script?.fingerprint ? `script ${fingerprintLabel(run.script)}` : 'script pending');
  return `${snapshot} · ${script}`;
};

const describeRowSummary = run => {
  if (run.status === 'published') {
    const publisher = `${run.publishedBy || run.requestedBy || '—'}${run.publishedVia === 'auto-publish' ? ' (auto-publish)' : ''}`;
    const latestPeriod = run.livePeriods?.[run.livePeriods.length - 1];
    return {
      primary: `Published by ${publisher}`,
      secondary: latestPeriod ? describeLivePeriod(latestPeriod) : `Published ${formatStamp(run.publishedAt)}`
    };
  }
  if (run.status === 'failed') return { primary: 'Failed', secondary: (run.errors || [])[0] || 'The run failed.' };
  if (run.status === 'needs_repair') {
    return { primary: 'Publish needs repair', secondary: run.publication?.error?.message || 'Retry the publish to resume.' };
  }
  if (run.kind === 'snapshot' && run.status === 'succeeded') {
    return {
      primary: 'Snapshot captured',
      secondary: `${Number(run.snapshot?.productCount || 0).toLocaleString()} products · ${Number(run.snapshot?.variantCount || 0).toLocaleString()} variants`
    };
  }
  if (run.status === 'ready') {
    const diff = run.diffSummary ? describeDiff(run.diffSummary) : null;
    return { primary: 'Ready', secondary: [diff, run.requestedBy ? `Created by ${run.requestedBy}` : null].filter(Boolean).join(' · ') };
  }
  return { primary: STATUS_WORDS[run.status] || 'Run', secondary: describeCurrentStep(run) };
};

const describeCurrentStep = run => {
  if (!run.currentStep) return null;
  const step = run.steps?.[run.currentStep] || {};
  const requestedSteps = Object.values(run.steps || {}).filter(candidate => candidate.status !== 'not_requested');
  const position = step.position || requestedSteps.findIndex(candidate => candidate === step) + 1;
  return position > 0 && requestedSteps.length
    ? `Step ${position} of ${requestedSteps.length} · ${stepLabel(run.currentStep)}`
    : stepLabel(run.currentStep);
};

const describeStep = (key, step) => {
  const facts = [`${step.position}. ${stepLabel(key)}`, String(step.status).replaceAll('_', ' ')];
  if (step.stage && step.stage !== step.status) facts.push(String(step.stage).replaceAll('_', ' '));
  const duration = formatDuration(step.startedAt, step.completedAt);
  if (duration) facts.push(duration);
  return facts.join(' · ');
};

const describeElapsed = run => {
  const endedAt = run.livePeriods?.[0]?.startedAt || run.completedAt || run.publishedAt;
  return formatDuration(run.queuedAt, endedAt) || '—';
};

const formatDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) return null;
  return formatMilliseconds(new Date(endedAt) - new Date(startedAt));
};

const formatMilliseconds = value => {
  const milliseconds = Math.max(0, Number(value));
  if (milliseconds < 1000) return `${Math.round(milliseconds)}ms`;
  const seconds = Math.max(1, Math.round(milliseconds / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return seconds % 60 ? `${minutes}m ${seconds % 60}s` : `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const describeSearchSync = run => {
  const algolia = run.applied?.sideEffects?.algolia || run.publication?.applyResult?.sideEffects?.algolia || run.publication?.error?.sideEffects?.algolia;
  if (!algolia) return null;
  if (algolia.status === 'failed') return 'Search sync failed';
  if (algolia.status === 'no_changes') {
    const facts = ['Search current', 'no changes'];
    if (algolia.existingObjects != null) facts.push(`${Number(algolia.existingObjects).toLocaleString()} objects compared`);
    if (algolia.deltaComputedInMs != null) facts.push(formatMilliseconds(algolia.deltaComputedInMs));
    return facts.join(' · ');
  }
  const upserts = algolia.upserts || {};
  const deletes = algolia.deletes || {};
  const taskIds = [...(upserts.taskIds || []), ...(deletes.taskIds || [])];
  const facts = [
    'Search accepted',
    `${formatCount(upserts.count, 'upsert')} in ${formatMilliseconds(upserts.acceptedInMs || 0)}`,
    `${formatCount(deletes.count, 'delete')} in ${formatMilliseconds(deletes.acceptedInMs || 0)}`
  ];
  if (taskIds.length) facts.push(`tasks ${taskIds.join(', ')}`);
  return facts.join(' · ');
};

const formatCount = (value, label) => {
  const count = Number(value || 0);
  return `${count.toLocaleString()} ${label}${count === 1 ? '' : 's'}`;
};

const describeOutcome = run => {
  if (run.status === 'published') {
    const applied = run.applied || {};
    const minted = Number(applied.variantsMinted || 0) + Number(applied.siblingsMinted || 0);
    const updated = Number(applied.variantsUpdated || 0) + Number(applied.siblingsUpdated || 0);
    const retired = Number(applied.variantsOrphaned || 0) + Number(applied.siblingsRetired || 0);
    const publisher = `${run.publishedBy || '—'}${run.publishedVia === 'auto-publish' ? ' (auto-publish)' : ''}`;
    return `Published ${formatStamp(run.publishedAt)} by ${publisher} · ${minted} minted, ${updated} rows refreshed, ${retired} retired`;
  }
  const stampedBy = `${STATUS_WORDS[run.status] || '—'} ${formatStamp(run.completedAt || run.queuedAt)} by ${run.requestedBy || '—'}`;
  if (run.status === 'needs_repair') return `${stampedBy} · ${run.publication?.error?.message || 'Publish stopped; retry to resume.'}`;
  if (run.status === 'failed') return `${stampedBy} · ${(run.errors || [])[0] || 'Run failed.'}`;
  if (run.kind === 'snapshot' && run.status === 'succeeded') {
    return `${stampedBy} · ${Number(run.snapshot?.productCount || 0).toLocaleString()} products, ${Number(run.snapshot?.variantCount || 0).toLocaleString()} variants`;
  }
  const facts = [stampedBy];
  if (run.stats) facts.push(`${Number(run.stats.materializedVariants || 0).toLocaleString()} variants from ${Number(run.stats.captureRows || 0).toLocaleString()} rows`);
  if (run.diffSummary) facts.push(describeDiff(run.diffSummary));
  if (run.autoPublishHoldMessage) facts.push(`Held: ${run.autoPublishHoldMessage}`);
  if (run.livePeriods?.length) facts.push(describeLivePeriod(run.livePeriods[run.livePeriods.length - 1]));
  return facts.join(' · ');
};

const describeLivePeriod = period =>
  period.endedAt
    ? `Live from ${formatStamp(period.startedAt)} to ${formatStamp(period.endedAt)}`
    : `Live since ${formatStamp(period.startedAt)}`;

const describeDiff = summary =>
  summary.changed
    ? `${summary.changed} changed (${summary.byType?.added || 0} added, ${summary.byType?.removed || 0} removed)`
    : 'no catalog changes';

export default CatalogRunHistory;
